import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Laptop } from "lucide-react";
import { useMemo } from "react";
import { AddConnectionButton } from "@/components/connectors/connections-browser";
import {
	AddConnectorMenu,
	CONNECTOR_LIST_CLASS,
	ConnectionRows,
	ConnectorBox,
	DeviceBoxes,
} from "@/components/connectors/connector-boxes";
import {
	ConnectorMark,
	resolveConnectorDisplay,
} from "@/components/connectors/connector-display";
import { ConnectorPageColumn } from "@/components/connectors/connector-page-shell";
import { facetLabels } from "@/components/connectors/facet-chips";
import {
	inferredPlatform,
	PlatformIcon,
} from "@/components/devices/device-display";
import { getAuthMethods } from "@/components/entity-tabs/connections-tab/auth-methods";
import { TabEmptyState } from "@/components/entity-tabs/tab-states";
import { OwnerWorkspaceGate } from "@/components/owner-workspace-gate";
import { Button } from "@/components/ui/button";
import {
	DetailParts,
	LIST_ROW_LAYOUT,
	ListRowBody,
} from "@/components/ui/list-row";
import { ListSkeleton } from "@/components/ui/list-skeleton";
import { ConnectorsIcon } from "@/components/ui/lobu-nav-icons";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAutomationsList } from "@/hooks/use-automations";
import type { AutomationRecord } from "@/lib/api/automations";
import {
	type ConnectionConnectorGroup,
	type ConnectionFacets,
	type ConnectionItem,
	type ConnectorDefinitionItem,
	type DeviceWorkerItem,
	type FeedItem,
	useAllFeeds,
	useAuthProfiles,
	useConnectionConnectorGroups,
	useConnections,
	useDevices,
	useInstallableConnectorCatalog,
} from "@/lib/api/connections";
import { useWorkspaceRoot } from "@/lib/api/entities";
import {
	type InferenceCatalogItem,
	type InferenceProviderItem,
	useInferenceProviderCatalog,
	useInferenceProviders,
} from "@/lib/api/inference-providers";
import { type SandboxItem, useSandboxes } from "@/lib/api/sandboxes";
import { offerableConnectorApps } from "@/lib/connectors/app-catalog";
import {
	automationsByConnection,
	feedsByConnection,
} from "@/lib/connectors/connection-links";
import {
	buildDeviceConnectorRows,
	connectorIsDeviceCapability,
	deviceInWorkspace,
} from "@/lib/connectors/device-rows";
import {
	CONNECTOR_FILTER_LABELS,
	CONNECTOR_FILTERS,
	type ConnectorFilter,
	connectorMatchesFilter,
	deriveConnectorFacets,
	parseConnectorFilter,
} from "@/lib/connectors/facets";
import {
	buildInferenceConnectorRows,
	INFERENCE_ROW_KEY_PREFIX,
} from "@/lib/connectors/inference-rows";
import {
	buildSandboxConnectorRows,
	SANDBOX_PROVIDER_KEY_PREFIX,
	sandboxProviderFaviconDomain,
	sandboxProviderLabel,
} from "@/lib/connectors/sandbox-rows";
import { gatewayOrigin } from "@/lib/gateway-origin";
import { OWNER_SECTIONS } from "@/lib/owner-sections";

export const Route = createFileRoute("/$owner/connectors/")({
	validateSearch: (
		search: Record<string, unknown>,
	): { filter?: ConnectorFilter; group?: "device" } => {
		const filter = parseConnectorFilter(search.filter);
		return {
			...(filter ? { filter } : {}),
			...(search.group === "device" ? { group: "device" as const } : {}),
		};
	},
	loader: ({ context: { queryClient }, params }) => {
		// Await nothing — every prefetch is fire-and-forget. The component
		// mounts on the first frame, and OwnerWorkspaceGate passes its loading
		// state through so the page chrome remains visible above the list skeleton.
		void queryClient.prefetchQuery(
			useWorkspaceRoot.queryOptions(params.owner, { slug: params.owner }),
		);
	},
	component: ConnectionsLandingPage,
});

function ConnectionsLandingPage() {
	const { owner } = Route.useParams();
	const { filter, group } = Route.useSearch();

	return (
		<OwnerWorkspaceGate owner={owner}>
			{(props) => (
				<ConnectionsLandingContent
					ownerSlug={props.ownerSlug}
					organizationId={props.organizationId}
					isWorkspaceLoading={props.isWorkspaceLoading}
					requestedFilter={filter}
					groupByDevice={group === "device"}
				/>
			)}
		</OwnerWorkspaceGate>
	);
}

function ConnectionsLandingContent({
	ownerSlug,
	organizationId,
	isWorkspaceLoading,
	requestedFilter,
	groupByDevice,
}: {
	ownerSlug: string;
	organizationId: string | null;
	isWorkspaceLoading: boolean;
	requestedFilter?: ConnectorFilter;
	groupByDevice: boolean;
}) {
	const {
		data: groups = [],
		isLoading: isGroupsLoading,
		isError: isGroupsError,
		refetch: refetchGroups,
	} = useConnectionConnectorGroups(organizationId ?? "");
	// The full installable catalog (installed org defs merged with bundled
	// catalog entries). The list renders only INSTALLED connectors (see the
	// items filter below); the catalog is still fetched here so uninstalled
	// entries can resolve display icons for any that slip through, and so the
	// "Add connector" page shares a warm cache. Browsing/adding uninstalled
	// connectors happens on /connectors/create, not here.
	const {
		data: installedDefs = [],
		isLoading: isDefsLoading,
		isError: isDefsError,
		refetch: refetchDefs,
	} = useInstallableConnectorCatalog(organizationId ?? "");
	const { data: authProfiles = [] } = useAuthProfiles(organizationId, {});
	const {
		data: inferenceProviders = [],
		isLoading: isProvidersLoading,
		isError: isProvidersError,
		refetch: refetchProviders,
	} = useInferenceProviders();
	// Provider rows join the inference catalog by `kind` for their icon — the
	// catalog is the only place a provider's logo lives.
	const { data: inferenceCatalog = [] } = useInferenceProviderCatalog();
	const {
		data: sandboxesPayload,
		isLoading: isSandboxesLoading,
		isError: isSandboxesError,
		refetch: refetchSandboxes,
	} = useSandboxes();
	// Devices are user-scoped and follow you across workspaces. A device belongs
	// on THIS list when it is attached here or already serves a connection here
	// — "relevant in this workspace", not merely "yours".
	const {
		data: allDevices = [],
		isLoading: isDevicesLoading,
		isError: isDevicesError,
		refetch: refetchDevices,
	} = useDevices();
	// What fills each box. None of these gate the page: a box renders from its
	// group summary first and fills in rows, feeds and Automations as they land.
	const { data: connections = [] } = useConnections(organizationId);
	const { data: feeds = [] } = useAllFeeds(organizationId);
	const { data: automations = [] } = useAutomationsList(
		organizationId ?? undefined,
	);
	const isContentLoading =
		isWorkspaceLoading ||
		isGroupsLoading ||
		isDefsLoading ||
		isProvidersLoading ||
		isSandboxesLoading ||
		isDevicesLoading;
	const sandboxes = sandboxesPayload?.sandboxes ?? [];
	const scopedDevices = useMemo(
		() => allDevices.filter((device) => deviceInWorkspace(device, ownerSlug)),
		[allDevices, ownerSlug],
	);

	if (
		isGroupsError ||
		isDefsError ||
		isProvidersError ||
		isSandboxesError ||
		isDevicesError
	)
		return (
			<ConnectorPageColumn>
				<div role="alert" className="space-y-2">
					<p>Could not load the connector inventory.</p>
					<Button
						variant="outline"
						size="sm"
						onClick={() => {
							void Promise.all([
								refetchGroups(),
								refetchDefs(),
								refetchProviders(),
								refetchSandboxes(),
								refetchDevices(),
							]);
						}}
					>
						Retry
					</Button>
				</div>
			</ConnectorPageColumn>
		);

	// Page padding comes from the connectors layout. The column is centred at
	// max-w-3xl, matching the agent pages and every connector/connection page.
	return (
		<ConnectorPageColumn className="flex flex-1 flex-col gap-6">
			<ConnectionsLandingInner
				ownerSlug={ownerSlug}
				isContentLoading={isContentLoading}
				groups={groups}
				installedDefs={installedDefs}
				authProfiles={authProfiles}
				inferenceProviders={inferenceProviders}
				inferenceCatalog={inferenceCatalog}
				sandboxes={sandboxes}
				devices={scopedDevices}
				requestedFilter={requestedFilter}
				groupByDevice={groupByDevice}
				connections={connections}
				feeds={feeds}
				automations={automations}
			/>
		</ConnectorPageColumn>
	);
}

function ConnectionsLandingInner({
	ownerSlug,
	isContentLoading,
	groups,
	installedDefs,
	authProfiles,
	inferenceProviders,
	inferenceCatalog,
	sandboxes,
	devices,
	requestedFilter,
	groupByDevice,
	connections,
	feeds,
	automations,
}: {
	ownerSlug: string;
	isContentLoading: boolean;
	groups: ConnectionConnectorGroup[];
	installedDefs: ConnectorDefinitionItem[];
	authProfiles: NonNullable<ReturnType<typeof useAuthProfiles>["data"]>;
	inferenceProviders: InferenceProviderItem[];
	inferenceCatalog: InferenceCatalogItem[];
	sandboxes: SandboxItem[];
	devices: DeviceWorkerItem[];
	requestedFilter?: ConnectorFilter;
	groupByDevice: boolean;
	connections: ConnectionItem[];
	feeds: FeedItem[];
	automations: AutomationRecord[];
}) {
	const navigate = useNavigate();
	const defsByKey = useMemo(
		() => new Map(installedDefs.map((d) => [d.key, d] as const)),
		[installedDefs],
	);
	const feedsByConn = useMemo(() => feedsByConnection(feeds), [feeds]);
	const automationsByConn = useMemo(
		() => automationsByConnection(automations),
		[automations],
	);
	const rowProps = {
		connectorDefs: defsByKey,
		feeds: feedsByConn,
		automations: automationsByConn,
		ownerSlug,
	};
	const selectedFilter = requestedFilter ?? "all";

	const allItems = useMemo(() => {
		const groupMap = new Map(groups.map((g) => [g.connector_key, g] as const));
		const defMap = new Map<string, ConnectorDefinitionItem>();
		for (const def of installedDefs) defMap.set(def.key, def);
		for (const group of groups) {
			if (!defMap.has(group.connector_key)) {
				defMap.set(group.connector_key, {
					key: group.connector_key,
					name: group.connector_name ?? group.connector_key,
					description: null,
					version: "",
					auth_schema: null,
					feeds_schema: null,
					actions_schema: null,
					options_schema: null,
					status: "active",
					login_enabled: false,
					favicon_domain: group.favicon_domain,
					facets: group.facets,
				} as ConnectorDefinitionItem & { facets?: ConnectionFacets });
			}
		}

		// Model-provider rows, synthesized from `inference_providers`. They carry
		// no group, no auth profiles, and no setup state — a provider without a
		// working key surfaces that as its own `status`, not as "needs setup".
		const providerItems = buildInferenceConnectorRows(inferenceProviders)
			.filter(
				(row, index, rows) =>
					rows.findIndex(
						(other) => other.provider.kind === row.provider.kind,
					) === index,
			)
			.map((row) => ({
				connector: {
					...row.connector,
					key: `${INFERENCE_ROW_KEY_PREFIX}${row.provider.kind}`,
					name:
						inferenceCatalog.find((c) => c.slug === row.provider.kind)
							?.displayName ?? row.provider.kind,
				},
				group: undefined,
				profileCounts: {
					env: 0,
					oauth_app: 0,
					oauth_account: 0,
					browser_session: 0,
				},
				facets: row.facets,
				needsSetup: false,
				provider: row.provider as InferenceProviderItem | undefined,
				sandbox: undefined as SandboxItem | undefined,
				device: undefined as DeviceWorkerItem | undefined,
				deviceFamily: undefined as
					| ReturnType<typeof buildDeviceConnectorRows>[number]
					| undefined,
			}));

		// Remote-sandbox rows, synthesized from `/sandboxes`. A missing vault
		// credential is `status: error` (and needsSetup), not a separate surface.
		const sandboxItems = buildSandboxConnectorRows(sandboxes)
			.filter(
				(row, index, rows) =>
					rows.findIndex(
						(other) => other.sandbox.providerKind === row.sandbox.providerKind,
					) === index,
			)
			.map((row) => ({
				connector: {
					...row.connector,
					key: `${SANDBOX_PROVIDER_KEY_PREFIX}${row.sandbox.providerKind}`,
					name: sandboxProviderLabel(row.sandbox.providerKind),
				},
				group: undefined,
				profileCounts: {
					env: 0,
					oauth_app: 0,
					oauth_account: 0,
					browser_session: 0,
				},
				facets: row.facets,
				needsSetup: sandboxes.some(
					(s) => s.providerKind === row.sandbox.providerKind && !s.connected,
				),
				provider: undefined as InferenceProviderItem | undefined,
				sandbox: row.sandbox,
				device: undefined as DeviceWorkerItem | undefined,
				deviceFamily: undefined as
					| ReturnType<typeof buildDeviceConnectorRows>[number]
					| undefined,
			}));

		// Paired devices — the local counterpart of a sandbox row. `needsSetup`
		// stays false: a device with no capabilities is a device, not an
		// unconfigured connector.
		const deviceItems = buildDeviceConnectorRows(
			devices,
			groups,
			ownerSlug,
		).map((row) => ({
			connector: row.connector,
			group: undefined,
			profileCounts: {
				env: 0,
				oauth_app: 0,
				oauth_account: 0,
				browser_session: 0,
			},
			facets: row.facets,
			needsSetup: false,
			isDevice: true,
			provider: undefined as InferenceProviderItem | undefined,
			sandbox: undefined as SandboxItem | undefined,
			device: row.device as DeviceWorkerItem | undefined,
			deviceFamily: row,
		}));

		const connectorItems = Array.from(defMap.values())
			// The list is the org's INSTALLED connectors only — the browsable catalog
			// of not-yet-added connectors lives on the "Add connector" page
			// (/connectors/create). A connector belongs here once it's installed or
			// has at least one connection (a group). Uninstalled catalog entries are
			// reachable via "Add connector", so nothing is stranded.
			.filter(
				(connector) =>
					connector.installed ||
					(groupMap.get(connector.key)?.connection_count ?? 0) > 0,
			)
			.filter((connector) => !connectorIsDeviceCapability(connector))
			.map((connector) => {
				const group = groupMap.get(connector.key);
				const methods = getAuthMethods(connector.auth_schema);
				const profileCounts = authProfiles.reduce(
					(acc, profile) => {
						if (profile.connector_key !== connector.key) return acc;
						acc[profile.profile_kind] += 1;
						return acc;
					},
					{ env: 0, oauth_app: 0, oauth_account: 0, browser_session: 0 },
				);
				const facets = deriveConnectorFacets(connector, group);
				const isDevice = devices.some((device) =>
					device.connectors.some(
						(c) =>
							c.organization_slug === ownerSlug &&
							c.connector_key === connector.key,
					),
				);
				const needsSetup = connectorNeedsSetup(
					connector,
					group,
					methods,
					profileCounts,
				);
				return {
					connector,
					group,
					profileCounts,
					facets,
					needsSetup,
					isDevice,
					provider: undefined as InferenceProviderItem | undefined,
					sandbox: undefined as SandboxItem | undefined,
					device: undefined as DeviceWorkerItem | undefined,
					deviceFamily: undefined as
						| ReturnType<typeof buildDeviceConnectorRows>[number]
						| undefined,
				};
			});

		return [
			...connectorItems,
			...providerItems,
			...sandboxItems,
			...deviceItems,
		].sort((a, b) => {
			// Every item here is installed or has connections, so "needs setup"
			// (installed-but-unconfigured) floats to the top as the actionable set.
			if (a.needsSetup && !b.needsSetup) return -1;
			if (!a.needsSetup && b.needsSetup) return 1;
			return a.connector.name.localeCompare(b.connector.name);
		});
	}, [
		ownerSlug,
		authProfiles,
		devices,
		groups,
		installedDefs,
		inferenceProviders,
		inferenceCatalog,
		sandboxes,
	]);

	// Only offer a chip that would actually narrow to something. The list was a
	// fixed row, so a workspace with two data connectors still showed Inference,
	// Runtime and On device — every one of them filtering to an empty page. A
	// chip that can only disappoint is worse than no chip. "All" stays whenever
	// there is more than one real choice.
	const availableFilters = useMemo(() => {
		const live = CONNECTOR_FILTERS.filter(
			(key) =>
				key !== "all" &&
				allItems.some((item) => connectorMatchesFilter(item, key)),
		);
		return live.length > 1 ? (["all", ...live] as ConnectorFilter[]) : [];
	}, [allItems]);

	// A chip can disappear under an active filter — remove the last device
	// connector while "On device" is selected and the page would filter to
	// nothing with no chip left to clear it. Fall back to "all" instead.
	const filter = availableFilters.includes(selectedFilter)
		? selectedFilter
		: "all";

	const items = useMemo(
		() => allItems.filter((item) => connectorMatchesFilter(item, filter)),
		[allItems, filter],
	);

	// What you could still connect: the Mac app, extension, and terminal/Docker
	// workers. Shown on top of the unfiltered list — device-backed options drop off
	// as soon as that platform has paired something.
	const appEntries = useMemo(
		() =>
			filter === "all"
				? offerableConnectorApps(ownerSlug, devices, gatewayOrigin())
				: [],
		[filter, ownerSlug, devices],
	);

	const addConnector = (
		<AddConnectorMenu ownerSlug={ownerSlug} catalog={installedDefs} />
	);

	// Reserved-footprint skeleton — the same bordered row list the loaded page
	// uses, so nothing reflows when the groups land.
	if (isContentLoading) {
		return (
			<div className="space-y-4">
				<div className="flex justify-end">{addConnector}</div>
				<ListSkeleton label="Loading connectors" rows={6} />
			</div>
		);
	}

	const selectGrouping = (byDevice: boolean) => {
		void navigate({
			to: "/$owner/connectors",
			params: { owner: ownerSlug },
			search: (current) => ({
				...current,
				group: byDevice ? ("device" as const) : undefined,
			}),
			replace: true,
		});
	};

	const selectFilter = (nextFilter: ConnectorFilter) => {
		void navigate({
			to: "/$owner/connectors",
			params: { owner: ownerSlug },
			search: (current) => ({
				...current,
				filter: nextFilter === "all" ? undefined : nextFilter,
			}),
			replace: true,
		});
	};

	return (
		<div className="space-y-4">
			{/* One control row: filters left, Add right. The Add button used to share
          a row with a three-line lede under justify-between, so on this column
          width it wrapped underneath and stopped reading as right-aligned.
          The lede itself named the Runtime and On device chips, which are no
          longer guaranteed to be on screen now the row is derived from what
          exists — so it says what the page is and stops there. */}
			<p className="text-sm text-muted-foreground">
				What your agents can reach — data, tools, models, and runtimes.
			</p>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
				<div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
					{availableFilters.map((key) => (
						<Button
							key={key}
							type="button"
							variant={filter === key ? "secondary" : "ghost"}
							aria-pressed={filter === key}
							size="sm"
							className="h-7 px-2 text-xs"
							onClick={() => selectFilter(key)}
						>
							{CONNECTOR_FILTER_LABELS[key]}
						</Button>
					))}
					{availableFilters.length === 0 &&
					items.length === 0 &&
					appEntries.length > 0 ? (
						// Fresh workspace: no chips (nothing to narrow to), so the
						// chip slot carries the ask instead of empty space — the setup
						// offers below are the empty state.
						<p className="text-sm text-muted-foreground">
							Nothing connected yet — set up one of the apps below.
						</p>
					) : null}
				</div>
				<div className="ml-auto flex shrink-0 items-center gap-2">
					<TooltipProvider delayDuration={200}>
						<fieldset
							aria-label="Group by"
							className="m-0 inline-flex min-w-0 rounded-md border p-0.5"
						>
							{[
								{
									byDevice: false,
									label: "Group by connector",
									icon: <ConnectorsIcon className="size-3.5" />,
								},
								{
									byDevice: true,
									label: "Group by device",
									icon: <Laptop className="size-3.5" />,
								},
							].map((option) => (
								<Tooltip key={option.label}>
									<TooltipTrigger asChild>
										<Button
											type="button"
											size="sm"
											variant={
												groupByDevice === option.byDevice
													? "secondary"
													: "ghost"
											}
											aria-pressed={groupByDevice === option.byDevice}
											aria-label={option.label}
											className="size-6 p-0"
											onClick={() => selectGrouping(option.byDevice)}
										>
											{option.icon}
										</Button>
									</TooltipTrigger>
									<TooltipContent>{option.label}</TooltipContent>
								</Tooltip>
							))}
						</fieldset>
					</TooltipProvider>
					{addConnector}
				</div>
			</div>

			{/* What you could still connect: the Mac app, the extension, and
			    the two worker flavors — one joined strip, icon + name only, on
			    top of the list. Dashed: these are not things this workspace
			    has. Each one drops off as soon as it has paired something. */}
			{appEntries.length > 0 ? (
				<div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-dashed bg-border sm:grid-cols-[repeat(auto-fit,minmax(10rem,1fr))]">
					{appEntries.map((app) => (
						<Link
							key={app.id}
							to="/$owner/connectors/app/$appId"
							params={{ owner: ownerSlug, appId: app.id }}
							title={app.tagline}
							className="flex min-w-0 items-center gap-2 bg-background px-3 py-2.5 text-muted-foreground transition-colors last:odd:col-span-2 hover:bg-accent/50 hover:text-foreground sm:last:odd:col-span-1"
						>
							<span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground">
								{app.icon}
							</span>
							<span className="truncate text-sm font-medium">{app.name}</span>
						</Link>
					))}
				</div>
			) : null}

			{groupByDevice ? (
				<DeviceBoxes
					{...rowProps}
					devices={devices}
					connections={connections}
				/>
			) : items.length === 0 ? (
				availableFilters.length > 0 ? (
					<TabEmptyState
						icon={OWNER_SECTIONS.connectors.icon}
						title="No connectors match this filter"
						description="Try another capability."
						secondaryActionLabel="Show all"
						secondaryOnAction={() => selectFilter("all")}
					/>
				) : appEntries.length >
					0 ? // Fresh workspace: the setup offers already are the empty
				// state — a "No connectors yet" banner over them would repeat
				// the same ask twice.
				null : (
					<TabEmptyState
						icon={OWNER_SECTIONS.connectors.icon}
						title="No connectors yet"
						description="Connect your first connector to this workspace."
					/>
				)
			) : (
				<div className={CONNECTOR_LIST_CLASS}>
					{items.map(
						({
							connector,
							group,
							facets,
							profileCounts,
							needsSetup,
							provider,
							sandbox,
							device,
							deviceFamily,
						}) => {
							const defsMap = new Map(
								installedDefs.map((d) => [d.key, d] as const),
							);
							// Provider rows have no connector definition; their icon joins
							// the inference catalog by `kind` (the provider's catalog
							// identity). Custom kinds miss the catalog and degrade to the
							// letter-mark. Sandbox rows use the sandbox name only.
							const display = resolveConnectorDisplay(connector.key, defsMap, {
								name: connector.name,
								iconUrl: provider
									? inferenceCatalog.find((c) => c.slug === provider.kind)
											?.iconUrl
									: undefined,
								faviconDomain:
									connector.favicon_domain ??
									group?.favicon_domain ??
									// Sandboxes have no definition and no catalog to join, so
									// their logo comes from the provider kind's domain.
									(sandbox
										? sandboxProviderFaviconDomain(sandbox.providerKind)
										: undefined),
							});
							const accountCount =
								profileCounts.oauth_account + profileCounts.browser_session;
							const connectionCount = group?.connection_count ?? 0;
							// Provider / sandbox rows have no connections/accounts to count —
							// their meta is the credential state that actually matters.
							const meta = device
								? `${deviceFamily?.devices.length ?? 1} ${device.platform === "chrome-extension" ? (deviceFamily?.devices.length === 1 ? "profile" : "profiles") : deviceFamily?.devices.length === 1 ? "device" : "devices"} · ${deviceFamily?.devices.filter((d) => d.online).length ?? 0} online`
								: provider
									? countLabel(
											inferenceProviders.filter((p) => p.kind === provider.kind)
												.length,
											"configured account",
											"configured accounts",
										)
									: sandbox
										? `${sandboxes.filter((s) => s.providerKind === sandbox.providerKind).length} instances`
										: [
												`${connectionCount} ${
													connectionCount === 1 ? "connection" : "connections"
												}`,
												accountCount > 0
													? `${accountCount} ${accountCount === 1 ? "saved authorization" : "saved authorizations"}`
													: null,
												profileCounts.oauth_app > 0
													? `${profileCounts.oauth_app} OAuth app${
															profileCounts.oauth_app === 1 ? "" : "s"
														}`
													: null,
											]
												.filter(Boolean)
												.join(" · ");
							const rowClassName = BOX_HEADER_CLASS;
							// Prefixed keys (inference / sandbox) route to the same detail
							// path; the detail page branches on the prefix.
							const rowBody = (
								<ListRowBody
									mark={
										device ? (
											<PlatformIcon platform={inferredPlatform(device)} />
										) : (
											<ConnectorMark
												icon={display.icon}
												iconUrl={display.iconUrl}
												name={display.name}
												faviconDomain={display.faviconDomain}
												className="h-7 w-7 text-[10px]"
											/>
										)
									}
									name={display.name}
									tone={needsSetup ? "amber" : null}
									details={
										<DetailParts
											parts={[
												needsSetup ? "Needs setup" : null,
												facetLabels(facets).join(", "),
												meta,
											]}
										/>
									}
								/>
							);

							const header = deviceFamily?.appId ? (
								<Link
									key={connector.key}
									to="/$owner/connectors/app/$appId"
									params={{ owner: ownerSlug, appId: deviceFamily.appId }}
									className={rowClassName}
								>
									{rowBody}
								</Link>
							) : device ? (
								<Link
									key={connector.key}
									to="/$owner/connectors/device/$deviceId"
									params={{ owner: ownerSlug, deviceId: device.id }}
									className={rowClassName}
								>
									{rowBody}
								</Link>
							) : (
								<Link
									key={connector.key}
									to="/$owner/connectors/$connectorKey"
									params={{ owner: ownerSlug, connectorKey: connector.key }}
									search={provider || sandbox ? { instances: true } : {}}
									className={rowClassName}
								>
									{rowBody}
								</Link>
							);
							const isConnector =
								!provider && !sandbox && !device && !deviceFamily;
							return (
								<ConnectorBox
									key={connector.key}
									header={header}
									action={
										isConnector ? (
											<AddConnectionButton
												ownerSlug={ownerSlug}
												connectorKey={connector.key}
												iconOnly
											/>
										) : undefined
									}
								>
									{isConnector ? (
										<ConnectionRows
											{...rowProps}
											connections={connections.filter(
												(c) => c.connector_key === connector.key,
											)}
										/>
									) : null}
								</ConnectorBox>
							);
						},
					)}
				</div>
			)}
		</div>
	);
}

function countLabel(n: number, one: string, many: string): string {
	return `${n} ${n === 1 ? one : many}`;
}

/** Box header chrome — every row kind uses it. */
const BOX_HEADER_CLASS = LIST_ROW_LAYOUT;

function connectorNeedsSetup(
	connector: ConnectorDefinitionItem,
	group: ConnectionConnectorGroup | undefined,
	methods: ReturnType<typeof getAuthMethods>,
	profileCounts: Record<
		"env" | "oauth_app" | "oauth_account" | "browser_session",
		number
	>,
): boolean {
	// "Needs setup" means an INSTALLED connector is missing a required binding
	// (credentials / OAuth app / app install / device) — NOT merely "not added".
	// Uninstalled catalog entries no longer render in this list, so being
	// uninstalled is not a setup state here.
	// Existing bindings are authoritative; shared OAuth apps need not belong to this connector key.
	if ((group?.connection_count ?? 0) > 0) return false;
	const hasNoAuth = methods.some((m) => m.type === "none");
	if (
		!hasNoAuth &&
		methods.some((m) => m.type === "oauth") &&
		profileCounts.oauth_app === 0
	) {
		return true;
	}
	if (
		!hasNoAuth &&
		methods.some((m) => m.type === "env_keys") &&
		profileCounts.env === 0
	) {
		return true;
	}
	if (
		!hasNoAuth &&
		methods.some((m) => m.type === "app_installation") &&
		!group
	) {
		return true;
	}
	// Device-bound connector (declares a required_capability) with no connection
	// yet: its runtime binding is unsatisfied. `!group` mirrors the app_install
	// branch above — resolveDeviceBinding guarantees no connection exists for
	// these without a valid device, so a group implies the binding is done.
	if (connector.required_capability && !group) {
		return true;
	}
	return false;
}
